// apps/web/src/features/russia-requests/components/editor/useRussiaDraftFiles.ts
//
// Local file-attachment state + REST sync for the Russia COMPLEX
// editor (#1546). Files attach to the editor's saved DRAFT
// request id, exactly like SIMPLE positions: the first upload calls
// `ensurePersisted` to materialize the draft id, then presign → PUT →
// bind. The China `RequestFilesField` upload/list/remove flow is mirrored
// (same content-type/size caps), retargeted to the Russia file endpoints.
//
// @aiSecurity signed download URLs are bearer-like — resolved on demand
//   into in-memory refs only by the consuming card; this hook never logs
//   or persists them, and the bind body's `objectKey` stays local.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RUSSIA_REQUEST_FILE_ALLOWED_MIME,
  RUSSIA_REQUEST_FILE_MAX_BYTES,
  RUSSIA_REQUEST_FILE_MAX_PER_REQUEST,
  type RussiaRequestFile,
  type RussiaRequestFileMime,
} from '@magimp/shared';
import {
  bindRussiaRequestFile,
  deleteRussiaRequestFile,
  listRussiaRequestFiles,
} from '../../../../api/russia-requests/files.js';
import { requestUploadUrl } from '../../../../api/requests/storage.js';
import { compressImage } from '../../../requests/draft-media/index.js';

const ALLOWED = new Set<string>(RUSSIA_REQUEST_FILE_ALLOWED_MIME);

function isAllowedMime(type: string): type is RussiaRequestFileMime {
  return ALLOWED.has(type);
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

/** Stable error codes mapped to i18n keys by the consuming card. */
export type RussiaDraftFilesError =
  | 'wrongType'
  | 'tooLarge'
  | 'uploadFailed'
  | 'listFailed'
  | 'removeFailed';

export interface UseRussiaDraftFilesOptions {
  /** Saved DRAFT id, or null before the first persist. */
  persistedId: string | null;
  /**
   * Ensure a persisted DRAFT request exists, returning its id (or null if
   * the form is not yet valid enough to create one). Called before the
   * first file bind.
   */
  ensurePersisted: () => Promise<string | null>;
}

export interface RussiaDraftFilesApi {
  files: RussiaRequestFile[];
  uploading: boolean;
  error: RussiaDraftFilesError | null;
  /** True once the per-request live-row cap is reached. */
  capReached: boolean;
  /** Compress (images) / size-gate, presign, PUT, then bind one file. */
  addFile: (file: File) => Promise<void>;
  removeFile: (fileId: string) => Promise<void>;
}

export function useRussiaDraftFiles(opts: UseRussiaDraftFilesOptions): RussiaDraftFilesApi {
  const { persistedId } = opts;
  const [files, setFiles] = useState<RussiaRequestFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<RussiaDraftFilesError | null>(null);

  const ensurePersistedRef = useRef(opts.ensurePersisted);
  ensurePersistedRef.current = opts.ensurePersisted;

  // Hydrate the list once a draft id exists (e.g. an autosave materialized
  //   it before the first upload).
  useEffect(() => {
    if (persistedId === null) {
      setFiles([]);
      return;
    }
    let cancelled = false;
    void listRussiaRequestFiles(persistedId).then(
      (rows) => {
        if (!cancelled) setFiles(rows);
      },
      () => {
        if (!cancelled) setError('listFailed');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [persistedId]);

  const capReached = files.length >= RUSSIA_REQUEST_FILE_MAX_PER_REQUEST;

  const addFile = useCallback(
    async (file: File): Promise<void> => {
      if (!isAllowedMime(file.type)) {
        setError('wrongType');
        return;
      }
      setError(null);
      setUploading(true);
      try {
        // Materialize the draft id before the first bind, exactly like the
        //   SIMPLE positions flow.
        const parentId = persistedId ?? (await ensurePersistedRef.current());
        if (parentId === null) {
          setError('uploadFailed');
          return;
        }
        // Images: re-encode to JPEG (cap applies to the COMPRESSED blob).
        // Documents: upload the original bytes AS-IS.
        let body: Blob;
        let contentType: RussiaRequestFileMime;
        if (isImage(file.type)) {
          body = await compressImage(file);
          contentType = 'image/jpeg';
        } else {
          body = file;
          contentType = file.type;
        }
        if (body.size > RUSSIA_REQUEST_FILE_MAX_BYTES) {
          setError('tooLarge');
          return;
        }
        const signed = await requestUploadUrl({
          scope: 'request-attachment',
          contentType,
          contentLength: body.size,
        });
        const put = await fetch(signed.uploadUrl, {
          method: 'PUT',
          body,
          headers: { 'content-type': contentType },
        });
        if (!put.ok) throw new Error(`upload failed: ${put.status}`);
        const created = await bindRussiaRequestFile(parentId, {
          objectKey: signed.objectKey,
          fileName: file.name,
          contentType,
          contentLength: body.size,
        });
        setFiles((prev) => [...prev, created]);
      } catch {
        setError('uploadFailed');
      } finally {
        setUploading(false);
      }
    },
    [persistedId],
  );

  const removeFile = useCallback(
    async (fileId: string): Promise<void> => {
      if (persistedId === null) return;
      try {
        await deleteRussiaRequestFile(persistedId, fileId);
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
      } catch {
        setError('removeFailed');
      }
    },
    [persistedId],
  );

  return { files, uploading, error, capReached, addFile, removeFile };
}
